/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { ToastContainer, toast } from 'react-toastify';
import Breadcrumbs from '../breadcrumbs/Breadcrumbs';
import 'react-toastify/dist/ReactToastify.css';
import './querycard.css';
import '../../styles/form-theme.css';

const COURSES_LIST = [
  { id: 'fullstack', name: 'JavaScript Fullstack Development' },
  { id: 'aws-devops', name: 'AWS Cloud with DevOps Essentials' },
  { id: 'frontend', name: 'Frontend Mastery' },
  { id: 'ai-copilot', name: 'AI-Assisted Development with GitHub Copilot' },
  { id: 'html-css-mastery', name: 'HTML & CSS Mastery' },
  { id: 'modern-javascript-mastery', name: 'Modern JavaScript Mastery' },
  { id: 'reactjs-mastery', name: 'React.js Mastery' },
  { id: 'angular-mastery', name: 'Angular Mastery' },
  { id: 'node-express-mastery', name: 'Node.js & Express.js Mastery' },
  { id: 'mean-mern-stack-project', name: 'MEAN/MERN Stack Project' },
];

const Querycard = ({ closeModal }) => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [contact, setContact] = useState('');
  const [message, setMessage] = useState('');
  const [course, setCourse] = useState('');
  const [fromEnroll, setFromEnroll] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isNameDirty, setIsNameDirty] = useState(false);
  const [isEmailDirty, setIsEmailDirty] = useState(false);
  const [isContactDirty, setIsContactDirty] = useState(false);
  const [isMessageDirty, setIsMessageDirty] = useState(false);
  const [isCourseDirty, setIsCourseDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check URL params for enroll flow
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const courseId = params.get('course');
    const isFromEnroll = params.get('fromEnroll') === 'true';

    if (isFromEnroll) {
      setFromEnroll(true);
    }

    if (isFromEnroll && courseId) {
      setCourse(courseId);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isFormValid) {
      toast.error('Please fill all required fields correctly', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    setIsSubmitting(true);

    // EmailJS service ID, template ID, and user ID
    const serviceId = 'service_wzcrsxp';
    const templateId = 'template_aszhm1n';
    const userId = '7lTaAJbQz06mpRC3i';

    const selectedCourse = course ? COURSES_LIST.find(c => c.id === course)?.name : 'Not Specified';

    const templateParams = {
      from_name: name,
      from_email: email,
      contact_number: contact,
      message: fromEnroll ? (message || 'Interested in this course') : message,
      course: fromEnroll ? selectedCourse : 'General Inquiry',
    };

    emailjs
      .send(serviceId, templateId, templateParams, userId)
      .then(() => {
        toast.success('Query sent successfully! We will get back to you soon.', {
          position: 'top-right',
          autoClose: 4000,
        });
        
        // Reset form fields
        setName('');
        setEmail('');
        setContact('');
        setMessage('');
        setCourse('');
        
        // Reset dirty states so no validation errors show
        setIsNameDirty(false);
        setIsEmailDirty(false);
        setIsContactDirty(false);
        setIsMessageDirty(false);
        setIsCourseDirty(false);
        
        setIsSubmitting(false);
        
        if (closeModal) {
          setTimeout(() => closeModal(), 1000);
        }
      })
      .catch((error) => {
        console.error('Email sending failed:', error);
        toast.error('Failed to send query. Please try again.', {
          position: 'top-right',
          autoClose: 3000,
        });
        setIsSubmitting(false);
      });
  };

  const validateForm = () => {
    const isNameValid = name.length >= 3 && name.length <= 50;
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isContactValid = /^[\d+]+$/.test(contact) && contact.length >= 10 && contact.length <= 12;
    
    // Different validation based on flow
    let isFormValidNow;
    if (fromEnroll) {
      // From enroll: name, email, contact, course required (message optional)
      const isCourseValid = course !== '';
      isFormValidNow = isNameValid && isEmailValid && isContactValid && isCourseValid;
    } else {
      // Normal flow: name, email, contact, message required (course not shown)
      const isMessageValid = message.length >= 10 && message.length <= 500;
      isFormValidNow = isNameValid && isEmailValid && isContactValid && isMessageValid;
    }

    setIsFormValid(isFormValidNow);
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
    setIsNameDirty(true);
    validateForm();
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setIsEmailDirty(true);
    validateForm();
  };

  const handleContactChange = (e) => {
    const inputValue = e.target.value;
    if (/^[\d+]*$/.test(inputValue)) {
      setContact(inputValue);
      setIsContactDirty(true);
      validateForm();
    }
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    setIsMessageDirty(true);
    validateForm();
  };

  const handleCourseChange = (e) => {
    setCourse(e.target.value);
    setIsCourseDirty(true);
    validateForm();
  };

  const isNameInvalid = isNameDirty && (name.length < 3 || name.length > 50);
  const isEmailInvalid = isEmailDirty && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isContactInvalid = isContactDirty && (!/^[\d+]+$/.test(contact) || contact.length < 10 || contact.length > 12);
  const isMessageInvalid = isMessageDirty && !fromEnroll && (message.length < 10 || message.length > 500);
  const isCourseInvalid = fromEnroll && isCourseDirty && course === '';

  const enrollBreadcrumbItems = [
    {
      label: 'Back',
      onClick: () => {
        if (window.history.length > 1) {
          navigate(-1);
        } else {
          navigate('/courses');
        }
      },
    },
    { label: 'Home', to: '/' },
    { label: 'Courses', to: '/courses' },
    { label: 'Roadmaps', to: '/courses-roadmaps' },
    { label: 'Enroll Form' },
  ];

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      <div className="query">
        <div className="query-content theme-form-surface" id="enroll-form">
          {fromEnroll && (
            <div className="query-breadcrumb-wrap">
              <Breadcrumbs items={enrollBreadcrumbItems} sticky={false} />
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">
                Name: <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={handleNameChange}
                minLength={3}
                maxLength={50}
                placeholder="Enter your full name"
                required
              />
              {isNameInvalid && <p className="warning">Name must be 3-50 characters.</p>}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email: <span className="required-asterisk">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                placeholder="Enter your email address"
                required
              />
              {isEmailInvalid && <p className="warning">Please enter a valid email.</p>}
            </div>

            <div className="form-group">
              <label htmlFor="contact">
                Contact Number: <span className="required-asterisk">*</span>
              </label>
              <input
                type="tel"
                id="contact"
                value={contact}
                onChange={handleContactChange}
                minLength={10}
                maxLength={12}
                placeholder="10-12 digits"
                required
              />
              {isContactInvalid && <p className="warning">Contact must be 10-12 digits.</p>}
            </div>

            {fromEnroll && (
              <div className="form-group">
                <label htmlFor="course">
                  Course Interest: <span className="required-asterisk">*</span>
                </label>
                <select
                  id="course"
                  value={course}
                  onChange={handleCourseChange}
                  required
                >
                  <option value="">-- Select a Course --</option>
                  {COURSES_LIST.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {isCourseInvalid && <p className="warning">Please select a course.</p>}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="message">
                Message: {!fromEnroll && <span className="required-asterisk">*</span>}
                {fromEnroll && <span style={{ color: '#999', fontSize: '0.9rem' }}>(Optional)</span>}
              </label>
              <textarea
                id="message"
                value={message}
                onChange={handleMessageChange}
                cols={55}
                rows={10}
                placeholder={fromEnroll ? 'Tell us more about your interest (optional)' : 'Enter your message (10-500 characters)'}
                style={{ resize: 'vertical' }}
                minLength={fromEnroll ? 0 : 10}
                maxLength={500}
                required={!fromEnroll}
              />
              {isMessageInvalid && (
                <p className="warning">Message must be 10-500 characters.</p>
              )}
            </div>

            <div className="form-buttons">
              <button 
                type="submit" 
                disabled={!isFormValid || isSubmitting}
                className={isFormValid ? '' : 'disabled'}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              {
                closeModal ? (
                  <button type="button" onClick={closeModal} disabled={isSubmitting}>
                    Cancel
                  </button>
                ) : null
              }
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Querycard;
